import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api, compressImageToWebP } from "../lib/api";
import { toast } from "sonner";
import { Upload, Camera, X } from "lucide-react";

export default function IdentifyScreen({ mode = "identify" }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const isDiagnose = mode === "diagnose";
  const accentColor = isDiagnose ? "#FF8F00" : "#4CAF50";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImageToWebP(file);
      setPreview(dataUrl);
    } catch {
      toast.error("Could not read image");
    }
  };

  const submit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const endpoint = isDiagnose ? "/diagnose" : "/identify";
      const { data } = await api.post(endpoint, { image: preview });
      toast.success(isDiagnose ? "Diagnosis complete" : "Plant identified");
      navigate(`/${mode}/result/${data.scan_id}`, { state: { result: data } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell section={isDiagnose ? "diagnose" : "identify"}>
      <div className="flex items-center justify-between mb-5 fade-up">
        <h1 className="text-2xl font-bold text-shadow-green">
          {isDiagnose ? "Disease Check" : "Identify Plant"}
        </h1>
        {isDiagnose && (
          <span
            className="glass-pill amber"
            data-testid="diagnose-mode-badge"
            style={{ background: "rgba(255,143,0,0.2)", border: "1px solid rgba(255,143,0,0.5)", boxShadow: "var(--glow-amber)", fontWeight: 700 }}
          >
            Disease Mode
          </span>
        )}
      </div>

      {!preview ? (
        <div className="space-y-5">
          <div
            className="relative glass-card aspect-[3/4] flex items-center justify-center overflow-hidden fade-up stagger-1"
            data-testid="capture-area"
            style={{ minHeight: 420 }}
          >
            {/* 260×260 scan frame with neon corners + scan line */}
            <div className="relative overflow-hidden" style={{ width: 260, height: 260 }}>
              <span className={`scan-corner tl ${isDiagnose ? "amber" : ""}`} />
              <span className={`scan-corner tr ${isDiagnose ? "amber" : ""}`} />
              <span className={`scan-corner bl ${isDiagnose ? "amber" : ""}`} />
              <span className={`scan-corner br ${isDiagnose ? "amber" : ""}`} />
              <span className={`scan-line ${isDiagnose ? "amber" : ""}`} />
              <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs px-4 text-center">
                Centre the plant inside the frame
              </div>
            </div>

            {/* First-use tip pill */}
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 glass-card px-3 py-1.5 text-[11px] text-soft whitespace-nowrap"
              style={{ borderColor: "rgba(76,175,80,0.3)" }}
            >
              💡 Good lighting helps.
            </div>
          </div>

          {/* Camera-style control row: gallery (40px), capture (72px), flash (40px) */}
          <div className="flex items-center justify-around fade-up stagger-2">
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="upload-photo-button"
              className="glass-card w-12 h-12 rounded-full flex items-center justify-center"
              aria-label="Upload from gallery"
            >
              <Upload size={18} style={{ color: accentColor }} />
            </button>

            <button
              onClick={() => fileRef.current?.click()}
              data-testid="take-photo-button"
              className="rounded-full flex items-center justify-center transition-transform active:scale-95"
              style={{
                width: 72, height: 72,
                background: "rgba(255,255,255,0.9)",
                border: `4px solid ${accentColor}`,
                boxShadow: isDiagnose ? "var(--glow-amber)" : "var(--glow-green-bright)",
              }}
              aria-label="Capture photo"
            >
              <Camera size={28} className="text-black" strokeWidth={1.5} />
            </button>

            <button
              data-testid="flash-toggle"
              className="glass-card w-12 h-12 rounded-full flex items-center justify-center"
              aria-label="Flash auto"
            >
              <span className="text-[10px] font-bold" style={{ color: accentColor }}>AUTO</span>
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="file-input"
          />
          <p className="text-center text-xs text-muted-soft fade-up stagger-3">
            {isDiagnose
              ? "Focus on affected leaves or stems for best diagnosis."
              : "Clear focus, single plant, neutral background."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="glass-card p-2 fade-up">
            <img src={preview} alt="preview" className="w-full rounded-xl" data-testid="preview-image" />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPreview(null)}
              disabled={busy}
              data-testid="retake-button"
              className="btn-outline flex-1 inline-flex items-center justify-center gap-2"
            >
              <X size={16} /> Retake
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="use-photo-button"
              className={isDiagnose ? "btn-amber flex-1" : "btn-primary flex-1"}
            >
              {busy ? "Analysing…" : "Use This Photo"}
            </button>
          </div>
          {busy && (
            <div className="glass-card p-4 flex items-center gap-3 fade-up" data-testid="ai-busy">
              <span className="w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" />
              <span className="text-soft text-sm">
                {isDiagnose ? "Inspecting leaves and stems…" : "Matching against 400K species…"}
              </span>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}
